import axiosInstance from '@/setup/axios/axiosInstance'
import { PrintTimesheetSalaryConfigGroup, PrintTimesheetSalaryConfigGroupPayload, PrintTimesheetSalaryConfigRecord, PrintTimesheetSalarySettings, PrintTimesheetSalarySettingsByGroup, SalaryFormula } from '../types/salary'
import { DEFAULT_PRINT_ORDER_SETTINGS } from '../templates/defaultPrintOrderSettings'
import { DEFAULT_PRINT_TIMESHEET_SALARY_SETTINGS } from '../templates/defaultPrintTimeSheetSalarySetings'
import { DEFAULT_PRINT_REVENUE_SETTINGS, PrintRevenueSettings } from '../templates/defaultPrintReVenueSettings'
import { PrintOrderSettings } from '../types/printOrder'

const PRINT_ORDER_SETTINGS_ENDPOINT = '/settings/print-config/'

const buildPrintConfigDetailEndpoint = (id: string | number): string => `${PRINT_ORDER_SETTINGS_ENDPOINT}${id}/`

const extractPrintConfigList = (response: any): any[] => {
	const data = response?.data?.results || response?.data?.items || response?.data?.data || response?.data
	return Array.isArray(data) ? data : []
}

const extractPrintConfigSingle = (response: any): any => {
	const payload = response?.data?.data || response?.data
	if (Array.isArray(payload)) {
		return payload[0] || null
	}

	if (payload && typeof payload === 'object' && Array.isArray((payload as any).results)) {
		return (payload as any).results[0] || null
	}

	return payload || null
}

const getFirstPrintConfigRecordByType = async (type: string): Promise<Record<string, any> | null> => {
	const response = await axiosInstance.get(PRINT_ORDER_SETTINGS_ENDPOINT, {params: {type}})
	const records = extractPrintConfigList(response)
	return records[0] || null
}

const createPrintTimesheetSalaryGroupId = (): string =>
	`timesheet-salary-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

const isLocalDraftPrintTimesheetSalaryGroup = (group: PrintTimesheetSalaryConfigGroup): boolean =>
	Boolean(group.local_key) || group.id === undefined || group.id === null

const normalizePrintOrderSettings = (raw?: Partial<PrintOrderSettings> | null): PrintOrderSettings => {
	const data = raw || {}
	return {
		title: data.title || (data as any).title || DEFAULT_PRINT_ORDER_SETTINGS.title,
		company_name: data.company_name || DEFAULT_PRINT_ORDER_SETTINGS.company_name,
		company_address: data.company_address || DEFAULT_PRINT_ORDER_SETTINGS.company_address,
		template_html: data.template_html || DEFAULT_PRINT_ORDER_SETTINGS.template_html,
		terms_title: data.terms_title || DEFAULT_PRINT_ORDER_SETTINGS.terms_title,
		terms_items: Array.isArray(data.terms_items) && data.terms_items.length > 0 ? data.terms_items : DEFAULT_PRINT_ORDER_SETTINGS.terms_items,
		warranty_title: data.warranty_title || DEFAULT_PRINT_ORDER_SETTINGS.warranty_title,
		warranty_description: data.warranty_description || DEFAULT_PRINT_ORDER_SETTINGS.warranty_description,
		warranty_extra_items: Array.isArray(data.warranty_extra_items) ? data.warranty_extra_items : DEFAULT_PRINT_ORDER_SETTINGS.warranty_extra_items,
		non_warranty_title: data.non_warranty_title || DEFAULT_PRINT_ORDER_SETTINGS.non_warranty_title,
		non_warranty_items: Array.isArray(data.non_warranty_items) && data.non_warranty_items.length > 0 ? data.non_warranty_items : DEFAULT_PRINT_ORDER_SETTINGS.non_warranty_items,
		footer_city_label: data.footer_city_label || DEFAULT_PRINT_ORDER_SETTINGS.footer_city_label,
		sales_staff_phone: data.sales_staff_phone || DEFAULT_PRINT_ORDER_SETTINGS.sales_staff_phone,
	}
}

const normalizePrintTimesheetSalarySettings = (raw?: Partial<PrintTimesheetSalarySettings> | null): PrintTimesheetSalarySettings => {
	const data = raw || {}
	const extraData = (data as any).extra_data || {}
	return {
		title: data.title || DEFAULT_PRINT_TIMESHEET_SALARY_SETTINGS.title,
		company_name: data.company_name || extraData.company_name || DEFAULT_PRINT_TIMESHEET_SALARY_SETTINGS.company_name,
		company_address: data.company_address || extraData.company_address || DEFAULT_PRINT_TIMESHEET_SALARY_SETTINGS.company_address,
		template_html: data.template_html || DEFAULT_PRINT_TIMESHEET_SALARY_SETTINGS.template_html,
		footer_city_label: data.footer_city_label || DEFAULT_PRINT_TIMESHEET_SALARY_SETTINGS.footer_city_label,
	}
}

const normalizePrintTimesheetSalaryConfigRecord = (raw?: Record<string, any> | null): PrintTimesheetSalaryConfigRecord => {
	const data = raw || {}
	const settings = normalizePrintTimesheetSalarySettings(data)

	return {
		id: data.id || null,
		config_type: data.config_type || data.type || 'print_timesheet_salary',
		group_name: data.group_name || '',
		group_note: data.group_note || '',
		...settings,
	}
}

const normalizePrintRevenueSettings = (raw?: Partial<PrintRevenueSettings> | null): PrintRevenueSettings => {
	const data = raw || {}
	return {
		title: data.title || DEFAULT_PRINT_REVENUE_SETTINGS.title,
		company_name: data.company_name || DEFAULT_PRINT_REVENUE_SETTINGS.company_name,
		company_address: data.company_address || DEFAULT_PRINT_REVENUE_SETTINGS.company_address,
		template_html: data.template_html || DEFAULT_PRINT_REVENUE_SETTINGS.template_html,
		footer_city_label: data.footer_city_label || DEFAULT_PRINT_REVENUE_SETTINGS.footer_city_label,
	}
}

export async function getPrintOrderSettings(type?: string): Promise<PrintOrderSettings> {
	const response = await axiosInstance.get(PRINT_ORDER_SETTINGS_ENDPOINT, type ? {params: {type}} : undefined)
	const payload = type ? extractPrintConfigList(response)[0] : extractPrintConfigSingle(response)
	return normalizePrintOrderSettings(payload)
}

export async function updatePrintOrderSettings(settings: PrintOrderSettings, type?: string): Promise<PrintOrderSettings> {
	const {company_name, company_address, sales_staff_phone, ...rest} = settings
	const payload = type ? {...rest, type} : rest
	const currentRecord = type ? await getFirstPrintConfigRecordByType(type) : null
	const response = currentRecord?.id
		? await axiosInstance.put(buildPrintConfigDetailEndpoint(currentRecord.id), payload)
		: await axiosInstance.post(PRINT_ORDER_SETTINGS_ENDPOINT, payload)
	const result = response?.data?.data || response?.data
	return normalizePrintOrderSettings(result)
}

export async function getPrintTimesheetSalarySettings(): Promise<PrintTimesheetSalarySettings> {
	const response = await axiosInstance.get(PRINT_ORDER_SETTINGS_ENDPOINT, {params: {type: 'print_timesheet_salary'}})
	const payload = extractPrintConfigList(response)[0]
	return normalizePrintTimesheetSalarySettings(payload)
}

export async function getPrintTimesheetSalaryConfigRecords(): Promise<PrintTimesheetSalaryConfigRecord[]> {
	const response = await axiosInstance.get(PRINT_ORDER_SETTINGS_ENDPOINT, {params: {type: 'print_timesheet_salary'}})
	const data = extractPrintConfigList(response)
	return Array.isArray(data) ? data.map((item) => normalizePrintTimesheetSalaryConfigRecord(item)) : []
}

export async function getPrintTimesheetSalaryConfigById(
	id: string | number
): Promise<PrintTimesheetSalaryConfigRecord | null> {
	const response = await axiosInstance.get(buildPrintConfigDetailEndpoint(id))
	const payload = extractPrintConfigSingle(response)
	if (!payload) {
		return null
	}

	return normalizePrintTimesheetSalaryConfigRecord(payload)
}

export async function updatePrintTimesheetSalarySettings(settings: PrintTimesheetSalarySettings): Promise<PrintTimesheetSalarySettings> {
	const payload = {
		title: settings.title,
		template_html: settings.template_html,
		footer_city_label: settings.footer_city_label,
		type: 'print_timesheet_salary',
	}
	const currentRecord = await getFirstPrintConfigRecordByType('print_timesheet_salary')
	const response = currentRecord?.id
		? await axiosInstance.put(buildPrintConfigDetailEndpoint(currentRecord.id), payload)
		: await axiosInstance.post(PRINT_ORDER_SETTINGS_ENDPOINT, payload)
	const result = response?.data?.data || response?.data
	return normalizePrintTimesheetSalarySettings(result)
}

export async function getPrintTimesheetSalaryConfigGroups(): Promise<PrintTimesheetSalaryConfigGroup[]> {
	const remoteGroups = await getPrintTimesheetSalaryConfigRecords()
	return remoteGroups.reduce<PrintTimesheetSalaryConfigGroup[]>((acc, item) => {
		if (!item.group_name) return acc
		if (acc.some((group) => group.name === item.group_name)) return acc

		acc.push({
			id: item.id || null,
			name: item.group_name,
			note: item.group_note || '',
		})
		return acc
	}, [])
}

export async function createPrintTimesheetSalaryConfigGroup(
	payload: PrintTimesheetSalaryConfigGroupPayload
): Promise<PrintTimesheetSalaryConfigGroup> {
	return {
		id: null,
		local_key: createPrintTimesheetSalaryGroupId(),
		name: payload.name.trim(),
		note: payload.note?.trim() || '',
	}
}

export async function getPrintTimesheetSalarySettingsByGroup(
	group: PrintTimesheetSalaryConfigGroup
): Promise<PrintTimesheetSalarySettingsByGroup> {
	if (!isLocalDraftPrintTimesheetSalaryGroup(group) && group.id !== undefined && group.id !== null) {
		const response = await axiosInstance.get(buildPrintConfigDetailEndpoint(group.id))
		const payload = extractPrintConfigSingle(response)
		const record = normalizePrintTimesheetSalaryConfigRecord(payload)

		return {
			group_id: group.id,
			config_id: record.id || null,
			group_name: record.group_name,
			group_note: record.group_note || '',
			...normalizePrintTimesheetSalarySettings(record),
		}
	}

	return {
		group_id: null,
		config_id: null,
		group_name: group.name,
		group_note: group.note || '',
		...DEFAULT_PRINT_TIMESHEET_SALARY_SETTINGS,
	}
}

export async function updatePrintTimesheetSalarySettingsByGroup(
	group: PrintTimesheetSalaryConfigGroup,
	settings: PrintTimesheetSalarySettings,
	groupMeta?: Partial<PrintTimesheetSalaryConfigGroup>
): Promise<PrintTimesheetSalarySettingsByGroup> {
	const isDraftGroup = isLocalDraftPrintTimesheetSalaryGroup(group)
	const payload = {
		title: settings.title,
		template_html: settings.template_html,
		footer_city_label: settings.footer_city_label,
		group_name: groupMeta?.name || group.name || '',
		group_note: groupMeta?.note || group.note || '',
		group_type: 'print_timesheet_salary',
		type: 'print_timesheet_salary',
		extra_data: {
			company_name: settings.company_name,
			company_address: settings.company_address,
		},
	}
	const response = isDraftGroup || !group.id
		? await axiosInstance.post(PRINT_ORDER_SETTINGS_ENDPOINT, payload)
		: await axiosInstance.put(buildPrintConfigDetailEndpoint(group.id), payload)
	const result = response?.data?.data || response?.data
	const record = normalizePrintTimesheetSalaryConfigRecord(result)

	return {
		group_id: record.id || null,
		config_id: record.id || null,
		group_name: record.group_name,
		group_note: record.group_note || '',
		...normalizePrintTimesheetSalarySettings(record),
	}
}

export async function deletePrintTimesheetSalaryConfigGroup(group: PrintTimesheetSalaryConfigGroup): Promise<void> {
	if (isLocalDraftPrintTimesheetSalaryGroup(group) || !group.id) return
	await axiosInstance.delete(buildPrintConfigDetailEndpoint(group.id))
}

export async function getPrintRevenueSettingsByType(type: string): Promise<PrintRevenueSettings> {
	const response = await axiosInstance.get(PRINT_ORDER_SETTINGS_ENDPOINT, {params: {type}})
	const payload = extractPrintConfigList(response)[0]
	return normalizePrintRevenueSettings(payload)
}

export async function getPrintRevenueSettings(): Promise<PrintRevenueSettings> {
	return getPrintRevenueSettingsByType('print_revenue')
}

export async function updatePrintRevenueSettings(settings: PrintRevenueSettings): Promise<PrintRevenueSettings> {
	const payload = {
		title: settings.title,
		template_html: settings.template_html,
		footer_city_label: settings.footer_city_label,
		type: 'print_revenue',
	}
	const currentRecord = await getFirstPrintConfigRecordByType('print_revenue')
	const response = currentRecord?.id
		? await axiosInstance.put(buildPrintConfigDetailEndpoint(currentRecord.id), payload)
		: await axiosInstance.post(PRINT_ORDER_SETTINGS_ENDPOINT, payload)
	const result = response?.data?.data || response?.data
	return normalizePrintRevenueSettings(result)
}

// Product Type Tax
export interface ProductTypeTaxItem {
	id?: string
	name: string
	value: number
	effective_date?: string
	is_default?: boolean
	is_active?: boolean
	is_sales_salary_revenue?: boolean
	is_survey_salary_revenue?: boolean
	is_cnc_salary_revenue?: boolean
}

const PRODUCT_TYPE_TAX_ENDPOINT = '/settings/update-product-type-tax/'

export async function getProductTypeTax(): Promise<ProductTypeTaxItem[]> {
	const response = await axiosInstance.get(PRODUCT_TYPE_TAX_ENDPOINT)
	const data = response?.data?.items || response?.data?.results || response?.data?.data || response?.data
	return Array.isArray(data) ? data : []
}

export async function updateProductTypeTax(items: ProductTypeTaxItem[]): Promise<ProductTypeTaxItem[]> {
	const response = await axiosInstance.post(PRODUCT_TYPE_TAX_ENDPOINT, {items})
	return response?.data?.data || response?.data
}

export async function deleteProductTypeTax(id: string): Promise<void> {
	await axiosInstance.delete(`${PRODUCT_TYPE_TAX_ENDPOINT}${id}/`)
}



const SALARY_FORMULA_ENDPOINT = '/settings/salary-formulas/'
let salaryFormulasCache: SalaryFormula[] | null = null
let salaryFormulasRequest: Promise<SalaryFormula[]> | null = null

function clearSalaryFormulasCache(): void {
	salaryFormulasCache = null
}

export async function getSalaryFormulas(): Promise<SalaryFormula[]> {
	if (salaryFormulasCache) {
		return salaryFormulasCache
	}

	if (salaryFormulasRequest) {
		return salaryFormulasRequest
	}

	salaryFormulasRequest = axiosInstance
		.get(SALARY_FORMULA_ENDPOINT)
		.then((response) => {
			const data = response?.data?.items || response?.data?.results || response?.data?.data || response?.data
			const normalized = Array.isArray(data) ? data : []
			salaryFormulasCache = normalized
			return normalized
		})
		.finally(() => {
			salaryFormulasRequest = null
		})

	return salaryFormulasRequest
}

export async function createSalaryFormula(formula: Omit<SalaryFormula, 'id'>): Promise<SalaryFormula> {
	clearSalaryFormulasCache()
	const response = await axiosInstance.post(SALARY_FORMULA_ENDPOINT, formula)
	return response?.data?.data || response?.data
}

export async function updateSalaryFormula(id: string, formula: Partial<SalaryFormula>): Promise<SalaryFormula> {
	clearSalaryFormulasCache()
	const response = await axiosInstance.put(`${SALARY_FORMULA_ENDPOINT}${id}/`, formula)
	return response?.data?.data || response?.data
}

export async function deleteSalaryFormula(id: string): Promise<void> {
	clearSalaryFormulasCache()
	await axiosInstance.delete(`${SALARY_FORMULA_ENDPOINT}${id}/`)
}

export async function bulkSaveSalaryFormulas(
	formulas: SalaryFormula[],
	options?: { print_template_salary_export?: string | null }
): Promise<SalaryFormula[]> {
	const response = await axiosInstance.post(`${SALARY_FORMULA_ENDPOINT}bulk-save/`, {
		items: formulas,
		print_template_salary_export: options?.print_template_salary_export ?? null,
	})
	const data = response?.data?.items || response?.data?.results || response?.data?.data || response?.data
	const normalized = Array.isArray(data) ? data : []
	salaryFormulasCache = normalized
	return normalized
}

// Salary Config Groups
export interface SalaryConfigGroupPayload {
	name: string
	note?: string
	order_number?: number
}

export interface SalaryConfigGroup {
	id?: string | number
	name: string
	note?: string
	order_number?: number
}

const SALARY_CONFIG_GROUP_ENDPOINT = '/settings/salary-config-groups/'

export async function getSalaryConfigGroups(): Promise<SalaryConfigGroup[]> {
	const response = await axiosInstance.get(SALARY_CONFIG_GROUP_ENDPOINT)
	const data = response?.data?.items || response?.data?.results || response?.data?.data || response?.data
	return Array.isArray(data) ? data : []
}

export async function getSalaryConfigGroupDetail(id: string | number): Promise<SalaryFormula[]> {
	const response = await axiosInstance.get(`${SALARY_CONFIG_GROUP_ENDPOINT}${id}/`)
	const payload = response?.data?.data || response?.data
	const formulas =
		payload?.salary_configs ||
		payload?.formulas ||
		payload?.salary_formulas ||
		payload?.items ||
		payload?.results ||
		payload?.data ||
		payload

	const normalizedFormulas = Array.isArray(formulas)
		? formulas.map((formula) => ({
			...formula,
			print_template_salary_id:
				formula?.print_template_salary_id ?? payload?.print_template_salary_export_id ?? null,
		}))
		: []

	;(normalizedFormulas as SalaryFormula[] & { print_template_salary_export_id?: string | null }).print_template_salary_export_id =
		payload?.print_template_salary_export_id ?? null

	return normalizedFormulas
}

export async function createSalaryConfigGroup(payload: SalaryConfigGroupPayload): Promise<SalaryConfigGroup> {
	const response = await axiosInstance.post(SALARY_CONFIG_GROUP_ENDPOINT, payload)
	return response?.data?.data || response?.data
}

export async function updateSalaryConfigGroup(id: string | number, payload: SalaryConfigGroupPayload): Promise<SalaryConfigGroup> {
	const response = await axiosInstance.put(`${SALARY_CONFIG_GROUP_ENDPOINT}${id}/`, payload)
	return response?.data?.data || response?.data
}

export async function deleteSalaryConfigGroup(id: string | number): Promise<void> {
	await axiosInstance.delete(`${SALARY_CONFIG_GROUP_ENDPOINT}${id}/`)
}

// Status Manage
export interface StatusManageItem {
	id?: string
	module: string
	name: string
	code?: string
	color?: string
	sort_order?: number
	is_default?: boolean
	is_record_sales?: boolean
	is_posted?: boolean
	is_active?: boolean
	group_status?: {
		id?: string
		name?: string
		code?: string
		description?: string | null
		sort_order?: number
		is_active?: boolean
	} | null
}

export interface StatusManageGroup {
	group_status: {
		id?: string
		name?: string
		code?: string
		description?: string | null
		sort_order?: number
		is_active?: boolean
	} | null
	items: StatusManageItem[]
}

export interface StatusManageModuleResponse {
	module: string
	groups: StatusManageGroup[]
	items: StatusManageItem[]
}

export interface SaveStatusesByModulePayload {
	module: string
	groups: StatusManageGroup[]
	items: StatusManageItem[]
}

const STATUS_MANAGE_ENDPOINT = '/settings/statuses/'

export async function getStatusesByModule(): Promise<Record<string, StatusManageModuleResponse>> {
	const response = await axiosInstance.get(`${STATUS_MANAGE_ENDPOINT}by-module/`)
	return response?.data || {}
}

export async function saveStatusesByModule(payload: SaveStatusesByModulePayload): Promise<StatusManageModuleResponse | Record<string, any>> {
	const endpoint = `${STATUS_MANAGE_ENDPOINT}by-module/`
	try {
		const response = await axiosInstance.put(endpoint, payload)
		return response?.data?.data || response?.data
	} catch (error) {
		const response = await axiosInstance.post(endpoint, payload)
		return response?.data?.data || response?.data
	}
}

export async function createStatus(payload: Omit<StatusManageItem, 'id' | 'code'>): Promise<StatusManageItem> {
	const response = await axiosInstance.post(STATUS_MANAGE_ENDPOINT, payload)
	return response?.data?.data || response?.data
}

export async function updateStatus(id: string, payload: Partial<Omit<StatusManageItem, 'code'>>): Promise<StatusManageItem> {
	const response = await axiosInstance.patch(`${STATUS_MANAGE_ENDPOINT}${id}/`, payload)
	return response?.data?.data || response?.data
}

export async function deleteStatus(id: string): Promise<void> {
	await axiosInstance.delete(`${STATUS_MANAGE_ENDPOINT}${id}/`)
}

export async function getModuleStatuses(module: string): Promise<StatusManageItem[]> {
	const response = await axiosInstance.get(STATUS_MANAGE_ENDPOINT, { params: { module, ordering: 'sort_order' } })
	const data = response?.data?.results || response?.data?.data || response?.data
	return Array.isArray(data) ? data : []
}

export type AmountSettingKey = 'site_allowance' | 'meal_deduction'

export interface AmountSetting {
	id: string
	key: AmountSettingKey
	amount: string
	created_at: string
	updated_at: string
}

export async function getAmountSetting(key: AmountSettingKey): Promise<AmountSetting> {
	const response = await axiosInstance.get(`/settings/amount-settings/${key}/`)
	return response.data
}

export async function updateAmountSetting(key: AmountSettingKey, amount: number): Promise<AmountSetting> {
	const response = await axiosInstance.put(`/settings/amount-settings/${key}/`, { amount })
	return response.data
}

// ─── Sales Channels ────────────────────────────────────────────────────────────

export interface SalesChannel {
	id?: string | null
	code?: string
	name: string
	description?: string
	color?: string
	sort_order?: number
	is_default?: boolean
	is_active?: boolean
}

const SALES_CHANNEL_ENDPOINT = '/orders/sales-channels/'

export async function getSalesChannels(): Promise<SalesChannel[]> {
	const response = await axiosInstance.get(SALES_CHANNEL_ENDPOINT, { params: { no_pagination: true } })
	const data = response?.data
	return Array.isArray(data) ? data : []
}

export async function bulkUpsertSalesChannels(items: SalesChannel[]): Promise<SalesChannel[]> {
	const response = await axiosInstance.post(`${SALES_CHANNEL_ENDPOINT}bulk-upsert/`, { items })
	const data = response?.data?.results || response?.data?.data || response?.data
	return Array.isArray(data) ? data : []
}

export async function deleteSalesChannel(id: string): Promise<void> {
	await axiosInstance.delete(`${SALES_CHANNEL_ENDPOINT}${id}/`)
}

// ─── Order report settings ─────────────────────────────────────────────────────

export interface OrderReportSetting {
	id?: string | null
	model?: string
	name: string
	status_id?: string
	status_ids?: string[]
	reward_date?: boolean
	sort_order?: number
}

const REPORT_SETTINGS_ENDPOINT = '/settings/report-settings/'
const ORDERS_REPORT_MODEL = 'orders'

const ordersReportSettingsParams = { model: ORDERS_REPORT_MODEL, no_pagination: true as const }

export async function getOrderReportSettings(): Promise<OrderReportSetting[]> {
	try {
		const response = await axiosInstance.get(REPORT_SETTINGS_ENDPOINT, {
			params: ordersReportSettingsParams,
		})
		const data = response?.data?.results || response?.data?.data || response?.data
		return Array.isArray(data) ? data : []
	} catch (error: any) {
		const status = error?.response?.status
		if (status === 404 || status === 405) {
			return []
		}
		throw error
	}
}

export async function bulkUpdateOrderReportSettings(items: OrderReportSetting[]): Promise<OrderReportSetting[]> {
	const response = await axiosInstance.post(
		`${REPORT_SETTINGS_ENDPOINT}bulk-update/`,
		{ items },
		{ params: { model: ORDERS_REPORT_MODEL } },
	)
	const data = response?.data?.results || response?.data?.data || response?.data
	return Array.isArray(data) ? data : []
}

export async function deleteOrderReportSetting(id: string): Promise<void> {
	await axiosInstance.delete(`${REPORT_SETTINGS_ENDPOINT}${id}/`, {
		params: { model: ORDERS_REPORT_MODEL },
	})
}

